import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { api, getTalkliveInstances } from '../services/api';
import ADFXmlEditor from './ADFXmlEditor';
import { normalizeSearch } from '../utils/search';

const DEFAULT_XML_TEMPLATE = `<?xml version="1.0" encoding="UTF-8"?>
<?adf version="1.0"?>
<adf>
  <prospect>
    <requestdate>{{date}}</requestdate>
    <vehicle interest="buy" status="{{vehicle_type}}">
      <year>{{year}}</year>
      <make>{{make}}</make>
      <model>{{model}}</model>
    </vehicle>
    <customer>
      <contact>
        <name part="first">{{lead_first_name}}</name>
        <name part="last">{{lead_last_name}}</name>
        <phone type="cell">{{lead_phone}}</phone>
        <email>{{lead_email}}</email>
      </contact>
      <comments><![CDATA[{{conversation}}]]></comments>
    </customer>
    <vendor>
      <contact>
        <name part="full">{{dealer_name}}</name>
      </contact>
    </vendor>
    <provider>
      <service>ChatIA</service>
      <url><![CDATA[{{source_url}}]]></url>
    </provider>
  </prospect>
</adf>`;

const TALKLIVE_SUB_INSTANCE_FALLBACK_PRESETS = [
  { value: 'sales', label: 'Ventes / Sales' },
  { value: 'service', label: 'Service' },
  { value: 'parts', label: 'Pièces / Parts' },
  { value: 'finance', label: 'Finance / F&I' },
  { value: 'body_shop', label: 'Carrosserie / Body Shop' },
];

const PRODUCT_OPTIONS = [
  { value: 'webchat', label: 'Webchat' },
  { value: 'talklive', label: 'TalkLive' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'facebook', label: 'Facebook Messenger' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'hook', label: 'Hook' },
  { value: 'phenix', label: 'Phenix (Legacy)' },
  { value: 'capture', label: 'Capture (Legacy alias)' },
];

const DealerADFConfig = ({ config, dealers, presets, sourceOptions, onSave, onClose }) => {
  const isNew = !config || (
    Object.keys(config.sources || {}).length === 0 &&
    Object.keys(config.products || {}).length === 0
  );

  const [assistantId, setAssistantId] = useState(config?.assistant_id || '');
  const [assistantName, setAssistantName] = useState(config?.assistant_name || '');

  // --- Sources state (Legacy) ---
  // Each source: { basePreset, xml_template, delay_seconds, migrating }
  const initEntryMap = (cfgMap) => {
    const result = {};
    for (const [key, src] of Object.entries(cfgMap || {})) {
      const delay = src.delay_seconds != null ? src.delay_seconds : 120;
      if (src.xml_template) {
        result[key] = { basePreset: src.preset || null, xml_template: src.xml_template, delay_seconds: delay, migrating: false };
      } else if (src.mapping) {
        result[key] = { basePreset: src.preset || null, xml_template: '', delay_seconds: delay, migrating: true, _mapping: src.mapping };
      } else if (src.preset && presets[src.preset]?.xml_template) {
        result[key] = { basePreset: src.preset, xml_template: presets[src.preset].xml_template, delay_seconds: delay, migrating: false };
      } else if (src.preset && presets[src.preset]?.mapping) {
        result[key] = { basePreset: src.preset, xml_template: '', delay_seconds: delay, migrating: true, _mapping: presets[src.preset].mapping };
      } else {
        result[key] = { basePreset: null, xml_template: DEFAULT_XML_TEMPLATE, delay_seconds: delay, migrating: false };
      }
    }
    return result;
  };

  const [sources, setSources] = useState(() => initEntryMap(config?.sources));
  const [products, setProducts] = useState(() => initEntryMap(config?.products));
  const [expandedSource, setExpandedSource] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [testEmailSource, setTestEmailSource] = useState(null);
  const [testEmailProduct, setTestEmailProduct] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testStatus, setTestStatus] = useState('');

  // --- TalkLive sub-instances ---
  const initSubInstances = () => {
    const subs = config?.talklive_sub_instances || {};
    const result = {};
    for (const [key, sub] of Object.entries(subs)) {
      result[key] = {
        label: sub.label || key,
        xml_template: sub.xml_template || DEFAULT_XML_TEMPLATE,
        delay_seconds: sub.delay_seconds != null ? sub.delay_seconds : 120,
      };
    }
    return result;
  };

  const [talkliveSubs, setTalkliveSubs] = useState(initSubInstances);
  const [expandedSub, setExpandedSub] = useState(null);
  const [newSubKey, setNewSubKey] = useState('');

  // --- Fetch real TalkLive instances for the selected dealer ---
  const [talkliveInstances, setTalkliveInstances] = useState([]);
  const [talkliveLoading, setTalkliveLoading] = useState(false);

  useEffect(() => {
    if (!assistantId) {
      setTalkliveInstances([]);
      return;
    }
    let cancelled = false;
    setTalkliveLoading(true);
    getTalkliveInstances(assistantId)
      .then((res) => {
        if (cancelled) return;
        const instances = Array.isArray(res.data) ? res.data : res.data?.instances || [];
        setTalkliveInstances(instances);
      })
      .catch(() => {
        if (!cancelled) setTalkliveInstances([]);
      })
      .finally(() => {
        if (!cancelled) setTalkliveLoading(false);
      });
    return () => { cancelled = true; };
  }, [assistantId]);

  // Build sub-instance presets from real TalkLive instances (fallback to static presets)
  const talkliveSubPresets = useMemo(() => {
    if (talkliveInstances.length > 0) {
      return talkliveInstances.map((inst) => ({
        value: inst.instance_id,
        label: inst.label || inst.instance_id,
      }));
    }
    return TALKLIVE_SUB_INSTANCE_FALLBACK_PRESETS;
  }, [talkliveInstances]);

  const presetEntries = Object.entries(presets);

  // --- Dealer select ---
  const dealerSelectOptions = useMemo(() => {
    const list = Array.isArray(dealers) ? dealers : [];
    return list.map((d) => ({
      value: d.assistant_id,
      label: `${d.name || d.assistant_id}  —  ${d.assistant_id}`,
      dealer: d,
    }));
  }, [dealers]);

  const filterOption = (option, inputValue) => {
    if (!inputValue) return true;
    const normalize = (s) => normalizeSearch(s).replace(/[_\-]/g, ' ');
    const haystack = normalize(option.label);
    const words = normalizeSearch(inputValue).split(/\s+/).filter(Boolean);
    return words.every((w) => haystack.includes(w));
  };

  const handleSelectDealer = (option) => {
    if (option) {
      setAssistantId(option.dealer.assistant_id);
      setAssistantName(option.dealer.name || '');
    } else {
      setAssistantId('');
      setAssistantName('');
    }
  };

  const selectedDealerOption = assistantId
    ? dealerSelectOptions.find((o) => o.value === assistantId) || { value: assistantId, label: `${assistantName || assistantId}  —  ${assistantId}` }
    : null;

  // --- Entry map operations (sources + products) ---

  const addEntry = (setter, key) => {
    setter((prev) => {
      if (prev[key]) return prev;
      return {
        ...prev,
        [key]: { basePreset: null, xml_template: DEFAULT_XML_TEMPLATE, delay_seconds: 120, migrating: false },
      };
    });
  };

  const removeEntry = (setter, key, expanded, setExpanded) => {
    setter((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    if (expanded === key) setExpanded(null);
  };

  const loadPresetIntoEntry = (setter, key, presetId) => {
    if (!presetId || !presets[presetId]) {
      setter((prev) => ({
        ...prev,
        [key]: { ...prev[key], basePreset: null },
      }));
      return;
    }
    const preset = presets[presetId];
    if (preset.xml_template) {
      setter((prev) => ({
        ...prev,
        [key]: { ...prev[key], basePreset: presetId, xml_template: preset.xml_template, migrating: false },
      }));
    } else if (preset.mapping) {
      setter((prev) => ({
        ...prev,
        [key]: { ...prev[key], basePreset: presetId, migrating: true, _mapping: preset.mapping },
      }));
      migrateMapping(setter, key, preset.mapping);
    }
  };

  const migrateMapping = async (setter, key, mapping) => {
    try {
      const res = await api.post('v1/adf/mapping-to-xml', { mapping });
      setter((prev) => ({
        ...prev,
        [key]: { ...prev[key], xml_template: res.data.xml_template || DEFAULT_XML_TEMPLATE, migrating: false, _mapping: undefined },
      }));
    } catch {
      setter((prev) => ({
        ...prev,
        [key]: { ...prev[key], xml_template: DEFAULT_XML_TEMPLATE, migrating: false, _mapping: undefined },
      }));
    }
  };

  const handleExpandEntry = (map, setter, key, expanded, setExpanded) => {
    const isExpanding = expanded !== key;
    setExpanded(isExpanding ? key : null);
    const entry = map[key];
    if (isExpanding && entry?.migrating && entry._mapping) {
      migrateMapping(setter, key, entry._mapping);
    }
  };

  const updateEntryXml = (setter, key, newXml) => {
    setter((prev) => ({
      ...prev,
      [key]: { ...prev[key], xml_template: newXml },
    }));
  };

  const updateEntryDelay = (setter, key, val) => {
    setter((prev) => ({
      ...prev,
      [key]: { ...prev[key], delay_seconds: val },
    }));
  };

  // --- TalkLive sub-instance operations ---

  const addSubInstance = (key) => {
    if (!key || talkliveSubs[key]) return;
    const presetLabel = talkliveSubPresets.find((p) => p.value === key)?.label || key;
    setTalkliveSubs((prev) => ({
      ...prev,
      [key]: { label: presetLabel, xml_template: DEFAULT_XML_TEMPLATE, delay_seconds: 120 },
    }));
    setExpandedSub(key);
    setNewSubKey('');
  };

  const removeSubInstance = (key) => {
    setTalkliveSubs((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    if (expandedSub === key) setExpandedSub(null);
  };

  const updateSubXml = (key, newXml) => {
    setTalkliveSubs((prev) => ({
      ...prev,
      [key]: { ...prev[key], xml_template: newXml },
    }));
  };

  // --- Save ---

  const entriesToSave = (map) => {
    const out = {};
    for (const [key, src] of Object.entries(map)) {
      out[key] = {
        preset: src.basePreset,
        overrides: null,
        mapping: null,
        xml_template: src.xml_template,
        delay_seconds: src.delay_seconds != null ? src.delay_seconds : 120,
      };
    }
    return out;
  };

  const handleSave = () => {
    if (!assistantId) {
      alert('Please select a dealer');
      return;
    }

    const saveData = {
      assistant_id: assistantId,
      assistant_name: assistantName,
      sources: entriesToSave(sources),
      products: entriesToSave(products),
    };

    if (Object.keys(talkliveSubs).length > 0) {
      saveData.talklive_sub_instances = talkliveSubs;
    }

    onSave(saveData, isNew);
  };

  const handleTestSend = async (sourceKey, xmlTemplate) => {
    if (!xmlTemplate) return;
    if (!testEmail.trim() || !testEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }
    setTestSending(true);
    setTestStatus('');
    try {
      await api.post('v1/adf/test-send', {
        xml_template: xmlTemplate,
        to_email: testEmail.trim(),
        context: assistantName ? { dealer_name: assistantName } : undefined,
      });
      setTestStatus(`Sent to ${testEmail}`);
      setTimeout(() => setTestStatus(''), 4000);
    } catch (err) {
      setTestStatus(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setTestSending(false);
    }
  };

  const configuredSourceKeys = Object.keys(sources);
  const availableSources = sourceOptions.filter((s) => !configuredSourceKeys.includes(s.value));
  const configuredProductKeys = Object.keys(products);
  const availableProducts = PRODUCT_OPTIONS.filter((p) => !configuredProductKeys.includes(p.value));
  const availableSubPresets = talkliveSubPresets.filter((p) => !talkliveSubs[p.value]);

  const inputStyle = { padding: '6px 8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' };

  const renderEntryList = ({
    title,
    subtitle,
    legacy,
    map,
    setter,
    keys,
    options,
    expanded,
    setExpanded,
    testKey,
    setTestKey,
    available,
    addLabel,
  }) => (
    <div style={{ marginBottom: '16px' }}>
      <h4 style={{ marginBottom: '4px' }}>
        {title}
        {legacy && (
          <span style={{
            marginLeft: '8px', fontSize: '11px', fontWeight: 'normal',
            background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: '3px',
          }}>
            Legacy
          </span>
        )}
      </h4>
      {subtitle && (
        <p style={{ fontSize: '12px', color: '#666', marginTop: 0, marginBottom: '12px' }}>{subtitle}</p>
      )}

      {keys.length === 0 ? (
        <p style={{ color: '#888', fontSize: '13px' }}>None configured. Add one below.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          {keys.map((entryKey) => {
            const entry = map[entryKey];
            const entryLabel = options.find((o) => o.value === entryKey)?.label || entryKey;
            const isExpanded = expanded === entryKey;
            return (
              <div key={entryKey} style={{
                border: '1px solid #ccc', borderRadius: '6px', overflow: 'hidden',
                background: isExpanded ? '#fff' : '#fafafa',
              }}>
                <div
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 12px', cursor: 'pointer',
                    background: isExpanded ? '#e8f0fe' : '#fafafa',
                    borderBottom: isExpanded ? '1px solid #ccc' : 'none',
                  }}
                  onClick={() => handleExpandEntry(map, setter, entryKey, expanded, setExpanded)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#666' }}>{isExpanded ? '▼' : '▶'}</span>
                    <strong style={{ fontSize: '14px' }}>{entryLabel}</strong>
                    {entry.basePreset && (
                      <span style={{
                        fontSize: '11px', background: '#d4edda', color: '#155724',
                        padding: '1px 6px', borderRadius: '3px',
                      }}>
                        {entry.basePreset}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeEntry(setter, entryKey, expanded, setExpanded);
                    }}
                    style={{ padding: '2px 8px', fontSize: '11px', color: '#c00', background: 'white', border: '1px solid #ddd', borderRadius: '3px' }}
                  >
                    Remove
                  </button>
                </div>

                {isExpanded && (
                  <div style={{ padding: '12px' }}>
                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Load from preset:</label>
                      <select
                        value={entry.basePreset || ''}
                        onChange={(e) => loadPresetIntoEntry(setter, entryKey, e.target.value)}
                        style={inputStyle}
                      >
                        <option value="">— None (custom) —</option>
                        {presetEntries.map(([pid, preset]) => (
                          <option key={pid} value={pid}>{preset.name || pid}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Delay before send:</label>
                      <input
                        type="range"
                        min="0" max="300" step="30"
                        value={entry.delay_seconds != null ? entry.delay_seconds : 120}
                        onChange={(e) => updateEntryDelay(setter, entryKey, parseInt(e.target.value, 10))}
                        style={{ width: '160px' }}
                      />
                      <span style={{ fontSize: '13px', fontFamily: 'monospace', minWidth: '55px' }}>
                        {entry.delay_seconds != null ? entry.delay_seconds : 120}s
                      </span>
                    </div>

                    {entry.migrating ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        Converting mapping to XML template...
                      </div>
                    ) : (
                      <ADFXmlEditor
                        xmlTemplate={entry.xml_template}
                        onChange={(newXml) => updateEntryXml(setter, entryKey, newXml)}
                      />
                    )}

                    <div style={{ marginTop: '10px' }}>
                      <button
                        onClick={() => {
                          if (testKey === entryKey) setTestKey(null);
                          else { setTestKey(entryKey); setTestStatus(''); }
                        }}
                        style={{ padding: '4px 12px', fontSize: '13px' }}
                      >
                        Send Test
                      </button>
                    </div>

                    {testKey === entryKey && (
                      <div style={{
                        marginTop: '8px', padding: '10px', background: '#f0f7ff',
                        borderRadius: '6px', border: '1px solid #b8d4f0',
                      }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="email@crm-provider.com"
                            style={{ ...inputStyle, flex: 1 }}
                          />
                          <button
                            onClick={() => handleTestSend(entryKey, entry.xml_template)}
                            disabled={testSending}
                            style={{
                              padding: '6px 14px', fontSize: '13px',
                              background: testSending ? '#ccc' : '#007bff', color: 'white',
                              border: 'none', borderRadius: '4px', cursor: testSending ? 'default' : 'pointer',
                            }}
                          >
                            {testSending ? 'Sending...' : 'Send'}
                          </button>
                        </div>
                        {testStatus && (
                          <div style={{
                            marginTop: '6px', fontSize: '12px',
                            color: testStatus.startsWith('Error') ? '#c00' : '#155724',
                          }}>
                            {testStatus}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {available.length > 0 && (
        <div>
          <label style={{ fontSize: '13px', fontWeight: 'bold', marginRight: '8px' }}>{addLabel}</label>
          {available.map((s) => (
            <button
              key={s.value}
              onClick={() => {
                addEntry(setter, s.value);
                setExpanded(s.value);
              }}
              style={{ padding: '4px 10px', fontSize: '12px', marginRight: '4px', marginBottom: '4px' }}
            >
              + {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      paddingTop: '20px', zIndex: 1000, overflowY: 'auto',
    }}>
      <div style={{
        background: 'white', borderRadius: '8px', padding: '24px', width: '940px',
        maxHeight: '94vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        marginBottom: '20px',
      }}>
        <h3 style={{ marginTop: 0 }}>
          {isNew && !config?.assistant_id ? 'New Dealer ADF Config' : `ADF Config: ${assistantName || assistantId}`}
        </h3>

        {/* Dealer selection */}
        {!config?.assistant_id ? (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '4px', display: 'block' }}>Select Dealer</label>
            <Select
              options={dealerSelectOptions}
              onChange={handleSelectDealer}
              value={selectedDealerOption}
              filterOption={filterOption}
              isClearable
              isSearchable
              placeholder="Search by name or assistant ID..."
              noOptionsMessage={() => 'No dealers found'}
              styles={{
                control: (base) => ({ ...base, fontSize: '13px' }),
                option: (base) => ({ ...base, fontSize: '13px' }),
              }}
            />
          </div>
        ) : (
          <div style={{ marginBottom: '16px', fontSize: '14px' }}>
            <span style={{ fontFamily: 'monospace', background: '#f0f0f0', padding: '2px 6px', borderRadius: '3px' }}>
              {assistantId}
            </span>
            {assistantName && <span style={{ marginLeft: '8px' }}>{assistantName}</span>}
          </div>
        )}

        {renderEntryList({
          title: 'Products',
          subtitle: 'ADF format per Agent product pack (Capture, TalkLive, Campaign, …). Preferred over legacy Sources.',
          legacy: false,
          map: products,
          setter: setProducts,
          keys: configuredProductKeys,
          options: PRODUCT_OPTIONS,
          expanded: expandedProduct,
          setExpanded: setExpandedProduct,
          testKey: testEmailProduct,
          setTestKey: setTestEmailProduct,
          available: availableProducts,
          addLabel: 'Add product:',
        })}

        {renderEntryList({
          title: 'Sources',
          subtitle: 'Legacy channel-based ADF configs (webchat, sms, facebook, …). Prefer Products above for new setups.',
          legacy: true,
          map: sources,
          setter: setSources,
          keys: configuredSourceKeys,
          options: sourceOptions,
          expanded: expandedSource,
          setExpanded: setExpandedSource,
          testKey: testEmailSource,
          setTestKey: setTestEmailSource,
          available: availableSources,
          addLabel: 'Add source:',
        })}

        {/* ============================================================ */}
        {/* TalkLive Sub-Instances */}
        {/* ============================================================ */}
        <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginTop: '8px' }}>
          <h4 style={{ marginBottom: '8px' }}>
            TalkLive Sub-Instances
            {talkliveLoading && <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888', marginLeft: '8px' }}>Loading instances...</span>}
          </h4>
          <p style={{ fontSize: '12px', color: '#666', marginTop: 0, marginBottom: '12px' }}>
            {talkliveInstances.length > 0
              ? `${talkliveInstances.length} instance(s) detected for this dealer. Click to add an ADF config per instance.`
              : 'Associate different ADF formats to TalkLive departments (sales, service, parts, etc.)'
            }
          </p>

          {Object.keys(talkliveSubs).length === 0 ? (
            <p style={{ color: '#888', fontSize: '13px' }}>No sub-instances configured.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              {Object.entries(talkliveSubs).map(([subKey, sub]) => {
                const isSubExpanded = expandedSub === subKey;
                return (
                  <div key={subKey} style={{
                    border: '1px solid #ccc', borderRadius: '6px', overflow: 'hidden',
                    background: isSubExpanded ? '#fff' : '#fafafa',
                  }}>
                    <div
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 12px', cursor: 'pointer',
                        background: isSubExpanded ? '#fef3e0' : '#fafafa',
                        borderBottom: isSubExpanded ? '1px solid #ccc' : 'none',
                      }}
                      onClick={() => setExpandedSub(isSubExpanded ? null : subKey)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>{isSubExpanded ? '▼' : '▶'}</span>
                        <strong style={{ fontSize: '14px' }}>{sub.label}</strong>
                        <span style={{
                          fontSize: '11px', background: '#fff3cd', color: '#856404',
                          padding: '1px 6px', borderRadius: '3px',
                        }}>
                          {subKey}
                        </span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeSubInstance(subKey); }}
                        style={{ padding: '2px 8px', fontSize: '11px', color: '#c00', background: 'white', border: '1px solid #ddd', borderRadius: '3px' }}
                      >
                        Remove
                      </button>
                    </div>

                    {isSubExpanded && (
                      <div style={{ padding: '12px' }}>
                        {/* Label */}
                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <label style={{ fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Label:</label>
                          <input
                            value={sub.label}
                            onChange={(e) => setTalkliveSubs((prev) => ({
                              ...prev,
                              [subKey]: { ...prev[subKey], label: e.target.value },
                            }))}
                            style={{ ...inputStyle, width: '200px' }}
                          />
                        </div>

                        {/* Delay */}
                        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <label style={{ fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Delay:</label>
                          <input
                            type="range"
                            min="0" max="300" step="30"
                            value={sub.delay_seconds != null ? sub.delay_seconds : 120}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              setTalkliveSubs((prev) => ({
                                ...prev,
                                [subKey]: { ...prev[subKey], delay_seconds: val },
                              }));
                            }}
                            style={{ width: '160px' }}
                          />
                          <span style={{ fontSize: '13px', fontFamily: 'monospace', minWidth: '55px' }}>
                            {sub.delay_seconds != null ? sub.delay_seconds : 120}s
                          </span>
                        </div>

                        {/* XML Editor */}
                        <ADFXmlEditor
                          xmlTemplate={sub.xml_template}
                          onChange={(newXml) => updateSubXml(subKey, newXml)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add sub-instance */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            {availableSubPresets.map((p) => (
              <button
                key={p.value}
                onClick={() => addSubInstance(p.value)}
                style={{ padding: '4px 10px', fontSize: '12px' }}
              >
                + {p.label}
              </button>
            ))}
            <span style={{ color: '#ccc', margin: '0 4px' }}>|</span>
            <input
              type="text"
              value={newSubKey}
              onChange={(e) => setNewSubKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              placeholder="custom_key"
              style={{ ...inputStyle, width: '120px', fontSize: '12px' }}
            />
            <button
              onClick={() => addSubInstance(newSubKey)}
              disabled={!newSubKey || talkliveSubs[newSubKey]}
              style={{ padding: '4px 10px', fontSize: '12px' }}
            >
              + Add
            </button>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
          <button onClick={onClose} style={{ padding: '8px 16px' }}>Cancel</button>
          <button onClick={handleSave} className="btn-primary" style={{ padding: '8px 16px' }}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default DealerADFConfig;
